import { Flex, Menu } from "antd"
import React, { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"

import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { invoicesHiddenForRole } from "../../utils/invoices-visibility.ts"
import { canAccessArchivePage } from "../Archive/archive-permissions.ts"
import { canSeeSignatureRequests } from "../Esign/helpers/signature-requests-access.ts"
import { canOpenLocations } from "../GlobalMap/helpers/locations-access.ts"
import { selectIsCompanyMode } from "../Branches/redux/branches.slice.ts"
import { type PermissionPages } from "../SettingsPermissions/types/permissions.types.ts"
import { SidebarContextMenu } from "./SidebarContextMenu.tsx"
import {
  type NavBadgeCounts,
  applyNotificationBadges
} from "./helpers/apply-notification-badges.tsx"
import {
  ARCHIVE_NAV_KEY,
  INVOICES_NAV_KEY,
  MAP_NAV_KEY,
  REPORTS_NAV_KEY,
  SIGNATURE_REQUESTS_NAV_KEY,
  type SidebarItem,
  groupNavItems,
  sidebarConfig,
  withGuideHint
} from "./sidebar-config.tsx"
import "./styles.less"

interface SidebarProps {
  onItemSelect: (item: Pick<SidebarItem, "key">) => void
  showDefaultOpenKeys?: boolean
}

// A nav key is its route, and the permission page is that route with the
// slashes swapped.
const navKeyToPage = (key: string): PermissionPages =>
  key?.replace(/\//g, "_") as PermissionPages

const openInNewTab = (route: string) => {
  window.open(route, "_blank", "noopener,noreferrer")
}

// Ctrl on Windows/Linux, Cmd on macOS — the browser's "open link in a new
// tab" modifier.
const hasNewTabModifier = (event: React.MouseEvent | React.KeyboardEvent) =>
  event.ctrlKey || event.metaKey

const MIDDLE_BUTTON = 1

// Company Overview's primary sidebar, per the explicit workspace-mode
// split — "home" is Dashboard's own nav key.
const COMPANY_MODE_KEYS = new Set([
  "home",
  REPORTS_NAV_KEY,
  "calendar",
  INVOICES_NAV_KEY,
  "team_management"
])

export const SidebarBody = memo(
  ({ onItemSelect, showDefaultOpenKeys }: SidebarProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const permissions = useAppSelector((state) => state.auth.permissions)
    const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
    const invoicesHidden = invoicesHiddenForRole(roleId)
    const isCompanyMode = useAppSelector(selectIsCompanyMode)
    const featureGlobalMap = useAppSelector(
      (state) => state.auth.userInfo?.feature_global_map
    )
    const pages = useMemo(
      () => [...(permissions?.pages || []), "document_templates"],
      [permissions?.pages]
    )

    const getAllKeysWithParent = (): Array<{
      key: string
      parentKey?: string
    }> => {
      const items = [...sidebarConfig.main, ...sidebarConfig.bottom]

      return items.flatMap((item) =>
        "children" in item && item.children
          ? item.children.map((c) => ({
              key: String(c?.key),
              parentKey: String(item.key)
            }))
          : [{ key: String(item.key) }]
      )
    }

    const openProject = useAppSelector((state) => state.project?.project)

    const currentKeys = useMemo(() => {
      // An archived project lives in the Archive, so that is where the
      // sidebar says you are while it is open.
      if (
        openProject?.is_archived &&
        canAccessArchivePage(permissions) &&
        location.pathname.startsWith(`/projects/${openProject.id}`)
      ) {
        return [ARCHIVE_NAV_KEY]
      }

      const path = location.pathname.slice(1)
      const allKeys = getAllKeysWithParent()

      const matched = allKeys
        .filter(({ key }) => path === key || path.startsWith(`${key}/`))
        .sort((a, b) => b.key.length - a.key.length)[0]

      if (!matched) return []

      return matched.parentKey
        ? [matched.parentKey, matched.key]
        : [matched.key]
    }, [
      location.pathname,
      openProject?.id,
      openProject?.is_archived,
      permissions
    ])

    const mainMenuItems: SidebarItem[] = useMemo(
      () =>
        sidebarConfig.main
          ?.reduce((list, item) => {
            if ("children" in item && item?.children?.length) {
              const filteredItems = item.children.filter((child) =>
                pages?.includes(navKeyToPage(child?.key as string))
              )

              if (filteredItems?.length) {
                list.push({ ...item, children: filteredItems })
              }
            } else {
              // Reports is open to every authenticated user.
              if (item?.key === REPORTS_NAV_KEY) {
                list.push(item)
              } else if (item?.key === INVOICES_NAV_KEY) {
                if (pages?.includes(INVOICES_NAV_KEY) && !invoicesHidden) {
                  list.push(item)
                }
              } else if (item?.key === MAP_NAV_KEY) {
                if (canOpenLocations(featureGlobalMap, pages)) list.push(item)
              } else if (item?.key === SIGNATURE_REQUESTS_NAV_KEY) {
                if (canSeeSignatureRequests(pages)) list.push(item)
              } else if (item?.key === ARCHIVE_NAV_KEY) {
                // Archive merges two former entry points — the "Inactive" leaf
                // under Leads and the Archive tab on Projects — so either
                // audience that could reach one of those still reaches this.
                if (canAccessArchivePage(permissions)) {
                  list.push(item)
                }
              } else if (pages?.includes(navKeyToPage(item?.key as string))) {
                list.push(item)
              }
            }

            return list
          }, [] as SidebarItem[])
          // Company Overview's primary sidebar is exactly five modules —
          // Leads/Projects/Archive/Approval/Customers/Subcontractors/
          // Suppliers keep their full authorized access underneath (routes
          // and permissions untouched), they are just not company-level
          // *navigation* while a multi-branch owner is looking at the whole
          // company rather than one branch.
          .filter(
            (item) => !isCompanyMode || COMPANY_MODE_KEYS.has(String(item.key))
          ),
      [pages, permissions, invoicesHidden, featureGlobalMap, isCompanyMode]
    )

    const projectsCount = useAppSelector(
      (state) => state.notificationCounts.projects
    )
    const calendarCount = useAppSelector(
      (state) => state.notificationCounts.calendar
    )
    const approvals = useAppSelector(
      (state) => state.notificationCounts.approvals
    )
    const subcontractorsCount = useAppSelector(
      (state) => state.notificationCounts.subcontractors
    )

    const navBadgeCounts: NavBadgeCounts = useMemo(
      () => ({
        projects: projectsCount,
        calendar: calendarCount,
        approval: approvals.total,
        proposals: approvals.proposals,
        contracts: approvals.contracts,
        document_reviews: approvals.documents,
        subcontractors: subcontractorsCount
      }),
      [projectsCount, calendarCount, approvals, subcontractorsCount]
    )

    // Permission filtering, then badges, then grouping — the headings are the
    // last step so they can never decide what is in the nav.
    const groupedMainMenuItems = useMemo(
      () =>
        groupNavItems(
          applyNotificationBadges(mainMenuItems, navBadgeCounts),
          t
        ),
      [mainMenuItems, navBadgeCounts, t]
    )

    const bottomMenuItems = useMemo(
      () => withGuideHint(sidebarConfig.bottom, t),
      [t]
    )

    const defaultOpenKeys = showDefaultOpenKeys ? currentKeys : []

    // Only a row that is a page maps to a route. A parent (Approval) has no
    // page of its own, so it is left out and never opens in a new tab.
    const routeByKey = useMemo(() => {
      const map = new Map<string, string>()
      const collect = (items: SidebarItem[]) => {
        items.forEach((item) => {
          if ("children" in item && item.children?.length) {
            item.children.forEach((child) => {
              const childKey = (child as { key?: React.Key })?.key
              if (childKey != null) map.set(String(childKey), `/${childKey}`)
            })
          } else {
            map.set(String(item.key), `/${item.key}`)
          }
        })
      }
      collect(mainMenuItems)
      collect(sidebarConfig.bottom)
      return map
    }, [mainMenuItems])

    const [contextMenu, setContextMenu] = useState<{
      x: number
      y: number
      route: string
    } | null>(null)

    // The route of the page row an event happened on, or null anywhere else —
    // including Approval's own title, whose data-menu-id matches no route.
    const resolveRoute = useCallback(
      (target: EventTarget): string | null => {
        const row =
          target instanceof Element
            ? target.closest<HTMLElement>("[data-menu-id]")
            : null
        if (!row) return null

        const menuId = row.getAttribute("data-menu-id") ?? ""
        const matchedKey = [...routeByKey.keys()].find(
          (key) => menuId === key || menuId.endsWith(`-${key}`)
        )
        return matchedKey ? (routeByKey.get(matchedKey) as string) : null
      },
      [routeByKey]
    )

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        setContextMenu({ x: event.clientX, y: event.clientY, route })
      },
      [resolveRoute]
    )

    // Ctrl/Cmd+click on a page row is caught on the way down, so the Menu's own
    // click — which would navigate this tab — never runs.
    const handleClickCapture = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (!hasNewTabModifier(event)) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        event.stopPropagation()
        openInNewTab(route)
      },
      [resolveRoute]
    )

    // The Menu fires its click from Enter too, so Ctrl/Cmd+Enter gets the same
    // treatment. Key repeats are swallowed rather than let through, or holding
    // the keys would navigate this tab after the first new one opens.
    const handleKeyDownCapture = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" || !hasNewTabModifier(event)) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        event.stopPropagation()
        if (!event.repeat) openInNewTab(route)
      },
      [resolveRoute]
    )

    const handleAuxClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (event.button !== MIDDLE_BUTTON) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        openInNewTab(route)
      },
      [resolveRoute]
    )

    // Pressing the middle button starts the browser's autoscroll on Windows;
    // over a row it means "open in a new tab" instead.
    const handleMouseDown = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (event.button === MIDDLE_BUTTON && resolveRoute(event.target)) {
          event.preventDefault()
        }
      },
      [resolveRoute]
    )

    const openContextMenuRoute = useCallback(() => {
      if (contextMenu) openInNewTab(contextMenu.route)
      setContextMenu(null)
    }, [contextMenu])

    return (
      <Flex
        vertical
        className={"h-full"}
        onContextMenu={handleContextMenu}
        onClickCapture={handleClickCapture}
        onKeyDownCapture={handleKeyDownCapture}
        onAuxClick={handleAuxClick}
        onMouseDown={handleMouseDown}
      >
        <SidebarContextMenu
          open={contextMenu != null}
          x={contextMenu?.x ?? 0}
          y={contextMenu?.y ?? 0}
          onOpenChange={(next) => {
            if (!next) setContextMenu(null)
          }}
          onOpenInNewTab={openContextMenuRoute}
        />
        {/* The nav scrolls on its own so a short viewport never puts a row out
            of reach, while the Guide anchor below it stays put. */}
        <nav
          aria-label={t("shell.nav.label")}
          className={
            "tailwind-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-2"
          }
        >
          <Menu
            mode="inline"
            selectedKeys={currentKeys}
            defaultOpenKeys={defaultOpenKeys}
            items={groupedMainMenuItems}
            onClick={onItemSelect}
            className="custom-sidebar-menu border-0"
            style={{ backgroundColor: "transparent" }}
          />
        </nav>

        <Menu
          mode="inline"
          selectedKeys={currentKeys}
          defaultOpenKeys={defaultOpenKeys}
          items={bottomMenuItems}
          onClick={onItemSelect}
          className="custom-sidebar-menu sidebar-anchor-menu shrink-0 border-0 border-t border-border py-2"
          style={{ backgroundColor: "transparent" }}
        />
      </Flex>
    )
  }
)

SidebarBody.displayName = "SidebarBody"
